import { useState, useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { getChannels } from '../ChannelsServices';

/**
 * Custom hook to handle channel fetching, pagination, search, and filters
 * @param {Function} handleError - Error handler function
 * @returns {Object} Channels data and fetch functions
 */
const useChannelFetching = (handleError, options = {}) => {
    const { initialPageSize = 25 } = options;
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: initialPageSize,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: [5, 10, 25, 50, 100],
    });
    const [searchString, setSearchString] = useState('');
    const [filters, setFilters] = useState([]);  // Array for SharedFilterBar
    const [normalizedFilters, setNormalizedFilters] = useState({});  // Object for API
    const [sorter, setSorter] = useState({
        field: null,
        order: null,
    });
    const fetchAbortControllerRef = useRef(null);
    const latestValuesRef = useRef({
        pagination,
        searchString,
        normalizedFilters,
        sorter,
    });

    // Base filter to hide virtual channels
    const baseFilters = { is_virtual: false };

    useEffect(() => {
        latestValuesRef.current = {
            pagination,
            searchString,
            normalizedFilters,
            sorter,
        };
    }, [pagination, searchString, normalizedFilters, sorter]);

    const fetchChannels = useCallback(async (page, pageSize, search, filterParams, sortField, sortOrder) => {
        const isInitial = page === 1 && !channels.length;
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        fetchAbortControllerRef.current = controller;

        if (isInitial) {
            setIsInitialLoading(true);
        }
        setLoading(true);

        try {
            // Merge base filters with user filters
            const mergedFilters = {
                ...baseFilters,
                ...(filterParams !== undefined ? filterParams : normalizedFilters)
            };

            const response = await getChannels({
                page: page || pagination.current,
                size: pageSize || pagination.pageSize,
                search: search !== undefined ? search : searchString,
                filters: mergedFilters,
                sort_by: sortField !== undefined ? sortField : sorter.field,
                sort_direction: sortOrder !== undefined ? sortOrder : sorter.order,
                signal: controller.signal,
            });

            setChannels(response.data || []);
            setPagination(prev => ({
                ...prev,
                current: response.metadata?.page || page || prev.current,
                pageSize: response.metadata?.page_size || pageSize || prev.pageSize,
                total: response.metadata?.total || 0,
            }));

            // Update sorter with API's default sort values
            const apiSortBy = response.metadata?.sort_by;
            const apiSortDirection = response.metadata?.sort_direction;

            if (apiSortBy || apiSortDirection) {
                setSorter({
                    field: apiSortBy || null,
                    order: apiSortDirection || null,
                });

                // On initial load, if API returns a default sort, initialize filters with that field
                if (isInitial && apiSortBy && filters.length === 0) {
                    const initialFilter = {
                        id: `${apiSortBy}-${Date.now()}`,
                        fieldKey: apiSortBy,
                        value: null,
                    };
                    setFilters([initialFilter]);
                }
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
            setChannels([]);
        } finally {
            if (fetchAbortControllerRef.current === controller) {
                setLoading(false);
            }
            if (isInitial && fetchAbortControllerRef.current === controller) {
                setIsInitialLoading(false);
            }
        }
    }, [handleError, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order, channels.length, filters.length]);

    const fetchChannelsRef = useRef(fetchChannels);

    useEffect(() => {
        fetchChannelsRef.current = fetchChannels;
    }, [fetchChannels]);

    const refetchChannels = useCallback(() => {
        fetchChannels(pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order);
    }, [fetchChannels, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order]);

    const debouncedSearchRef = useRef(null);
    const debouncedFiltersRef = useRef(null);

    useEffect(() => {
        debouncedSearchRef.current = debounce((value) => {
            const { pagination, normalizedFilters, sorter } = latestValuesRef.current;
            fetchChannelsRef.current(1, pagination.pageSize, value, normalizedFilters, sorter.field, sorter.order);
        }, 500);

        debouncedFiltersRef.current = debounce((normalizedFiltersObject) => {
            const { pagination, searchString, sorter } = latestValuesRef.current;
            fetchChannelsRef.current(1, pagination.pageSize, searchString, normalizedFiltersObject, sorter.field, sorter.order);
        }, 500);

        return () => {
            debouncedSearchRef.current?.cancel?.();
            debouncedFiltersRef.current?.cancel?.();
        };
    }, []);

    const handleTableChange = useCallback((newPagination, tableFilters, tableSorter) => {
        const sortField = tableSorter.field || null;
        const sortOrder = tableSorter.order === 'ascend' ? 'asc' : tableSorter.order === 'descend' ? 'desc' : null;

        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        setSorter({
            field: sortField,
            order: sortOrder,
        });

        fetchChannels(newPagination.current, newPagination.pageSize, searchString, normalizedFilters, sortField, sortOrder);
    }, [fetchChannels, searchString, normalizedFilters]);

    const handleSearch = useCallback((value) => {
        setSearchString(value);
        setPagination(prev => ({ ...prev, current: 1 }));
        debouncedSearchRef.current?.(value);
    }, []);

    const handleFilterChange = useCallback((filtersArray, normalizedFiltersObject) => {
        setFilters(filtersArray);  // Store the array for SharedFilterBar
        setNormalizedFilters(normalizedFiltersObject);  // Store the object for API
        setPagination(prev => ({ ...prev, current: 1 }));
        debouncedFiltersRef.current?.(normalizedFiltersObject);
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchChannels(1, pagination.pageSize);
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
            debouncedSearchRef.current?.cancel?.();
            debouncedFiltersRef.current?.cancel?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        channels,
        setChannels,
        loading,
        isInitialLoading,
        pagination,
        searchString,
        filters,
        sorter,
        fetchChannels,
        refetchChannels,
        handleTableChange,
        handleSearch,
        handleFilterChange,
    };
};

export default useChannelFetching;
